import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { AccountAirdropSummary } from '../types'
import {
  AirdropBreakdownQueryData,
  airdropBreakdownQueryFunction,
  airdropBreakdownQueryKey,
} from './airdropBreakdownQuery'
import { AirdropClaimsQueryData, airdropClaimsQueryFunction, airdropClaimsQueryKey } from './airdropClaimsQuery'
import { getAllocationsSummary } from './getAllocationsSummary'
import { spkStakeAmountQueryFunction, spkStakeAmountQueryKey } from './spkStakeAmountQuery'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'

export interface UseSpkAirdropDataParams {
  chainId: number
}

export type UseSpkAirdropDataResult = {
  accountAirdropSummary: AccountAirdropSummary
  accountAirdropClaims: AirdropClaimsQueryData
  accountSpkStakedAmount: NormalizedNumber
}

export function useSpkAirdropData({ chainId }: UseSpkAirdropDataParams): UseSpkAirdropDataResult {
  const wagmiConfig = useConfig()
  const { account: address } = useConnectedAddress({ throwOnNotConnected: false })
  const spk = useSpkTokenRepository({ chainId, account: address }).findOneBySymbol(TokenSymbol('SPK'))

  const [allocationsSummaryResult, accountAirdropClaimsResult, accountSpkStakedAmountResult] = useSuspenseQueries({
    queries: [
      {
        queryKey: airdropBreakdownQueryKey({ address }),
        queryFn: () => airdropBreakdownQueryFunction({ address, wagmiConfig, chainId }),
      },
      {
        queryKey: airdropClaimsQueryKey({ address, chainId }),
        queryFn: () => airdropClaimsQueryFunction({ address, chainId, wagmiConfig, spk }),
      },
      {
        queryKey: spkStakeAmountQueryKey({ address, chainId }),
        queryFn: () => spkStakeAmountQueryFunction({ address, chainId, wagmiConfig, spk }),
      },
    ],
  })

  const accountAirdropSummary = getAccountAirdropSummary(allocationsSummaryResult.data, accountAirdropClaimsResult.data)

  return {
    accountAirdropSummary,
    accountAirdropClaims: accountAirdropClaimsResult.data,
    accountSpkStakedAmount: accountSpkStakedAmountResult.data,
  }
}

function getAccountAirdropSummary(
  allocationsSummaryResult: AirdropBreakdownQueryData,
  claimsResult: AirdropClaimsQueryData,
): AccountAirdropSummary {
  const allocationsSummary = getAllocationsSummary(allocationsSummaryResult, claimsResult)

  if (!allocationsSummary || !claimsResult) {
    // this object shouldn't be used anywhere to render anything, it's just a placeholder
    return {
      airdrops: [],
      totalUnclaimed: NormalizedNumber.ZERO,
      totalClaimed: NormalizedNumber.ZERO,
      totalAllocation: NormalizedNumber.ZERO,
      ignitionAllocation: NormalizedNumber.ZERO,
      overdriveAllocation: NormalizedNumber.ZERO,
    }
  }

  return {
    ...allocationsSummary,
    totalUnclaimed: allocationsSummary.totalAllocation.minus(claimsResult.total),
    totalClaimed: claimsResult.total,
  }
}
